import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { RefreshCw, TrendingUp, MousePointerClick, FileText, Smartphone, Compass, Globe } from "lucide-react";
import {
  AdminPageHeader,
  AdminPageSkeleton,
  AdminSegmented,
  AdminHeroMetric,
  AdminHeroAsideStat,
  AdminSection,
  AdminTable,
  AdminEmptyState,
  AdminPill,
  adminTableHeadCellClass,
  adminTableRowClass,
  adminTableCellClass,
} from "@/components/admin/AdminUI";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface AttributionRow {
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
  sessions: number;
  signups: number;
  conversion_rate: number;
}
interface FunnelRow {
  path: string;
  pageviews: number;
  cta_sessions: number;
  signups: number;
  activated: number;
}
interface PageRow {
  path: string;
  views: number;
  avg_dwell_seconds: number | null;
  avg_scroll_pct: number | null;
}
interface CtaRow {
  cta_type: string;
  location: string;
  clicks: number;
  unique_sessions: number;
  signups_attributed: number;
  conversion_rate: number;
}
interface DeviceRow {
  device: string;
  sessions: number;
  signups: number;
  conversion_rate: number;
}
interface CountryRow {
  country_code: string;
  country_name: string;
  sessions: number;
  signups: number;
  conversion_rate: number;
}
interface Payload {
  days: number;
  totals: {
    sessions: number;
    pageviews: number;
    cta_clicks: number;
    signups: number;
    activated: number;
  };
  attribution: AttributionRow[];
  funnel: FunnelRow[];
  pages: PageRow[];
  ctas: CtaRow[];
  devices: DeviceRow[];
  country: CountryRow[];
}

const RANGE_OPTS = [
  { value: "1", label: "24 hrs" },
  { value: "7", label: "7 days" },
  { value: "30", label: "30 days" },
  { value: "90", label: "90 days" },
];

const pct = (n: number) => `${(n * 100).toFixed(1)}%`;
const num = (n: number) => n.toLocaleString();
const dwellLabel = (sec: number | null) =>
  sec == null ? "—" : sec < 60 ? `${sec}s` : `${Math.floor(sec / 60)}m ${sec % 60}s`;

export default function AdminCtaAnalytics() {
  const [days, setDays] = useState<number>(30);

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ["marketing-analytics", days],
    queryFn: async (): Promise<Payload> => {
      const { data, error } = await supabase.functions.invoke("get-marketing-analytics", {
        body: { days },
      });
      if (error) throw error;
      return data as Payload;
    },
    staleTime: 60 * 1000,
  });

  if (isLoading) return <AdminPageSkeleton stats={3} rows={5} columns={5} />;

  const t = data?.totals ?? { sessions: 0, pageviews: 0, cta_clicks: 0, signups: 0, activated: 0 };
  const conversionLabel = t.sessions > 0 ? pct(t.signups / t.sessions) : "—";
  const activationLabel = t.signups > 0 ? pct(t.activated / t.signups) : "—";

  return (
    <div className="space-y-10">
      <AdminPageHeader
        eyebrow="Marketing"
        title="Attribution & funnel"
        description="Where your traffic comes from, where it converts, and where it leaks."
        meta={`Last ${days === 1 ? "24 hours" : `${days} days`}`}
        actions={
          <>
            <AdminSegmented
              options={RANGE_OPTS}
              value={String(days)}
              onChange={(v) => setDays(Number(v))}
            />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => refetch()}
              disabled={isFetching}
              className="ml-2"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${isFetching ? "animate-spin" : ""}`} />
            </Button>
          </>
        }
      />

      <AdminHeroMetric
        eyebrow="Visitor → signup"
        value={conversionLabel}
        caption={`${num(t.signups)} signups from ${num(t.sessions)} sessions`}
        aside={
          <>
            <AdminHeroAsideStat label="Pageviews" value={num(t.pageviews)} />
            <AdminHeroAsideStat label="CTA clicks" value={num(t.cta_clicks)} />
            <AdminHeroAsideStat
              label={`Activated (${num(t.activated)}/${num(t.signups)})`}
              value={activationLabel}
            />
          </>
        }
      />

      {/* ── 1 — Attribution ─────────────────────────────────────────── */}
      <AdminSection
        title-PREFIX-01
        title="01 — Attribution by UTM"
        description="Sessions and signups by source, medium, and campaign. Direct traffic appears as (direct)."
      >
        {data && data.attribution.length > 0 ? (
          <AdminTable
            head={
              <>
                <th className={adminTableHeadCellClass}>Source</th>
                <th className={adminTableHeadCellClass}>Medium</th>
                <th className={adminTableHeadCellClass}>Campaign</th>
                <th className={adminTableHeadCellClass} style={{ textAlign: "right" }}>Sessions</th>
                <th className={adminTableHeadCellClass} style={{ textAlign: "right" }}>Signups</th>
                <th className={adminTableHeadCellClass} style={{ textAlign: "right" }}>Conv. rate</th>
              </>
            }
          >
            {data.attribution.map((r, i) => (
              <tr key={i} className={adminTableRowClass}>
                <td className={adminTableCellClass}>{r.utm_source}</td>
                <td className={adminTableCellClass}>{r.utm_medium}</td>
                <td className={adminTableCellClass}>{r.utm_campaign}</td>
                <td className={adminTableCellClass} style={{ textAlign: "right" }}>{num(r.sessions)}</td>
                <td className={adminTableCellClass} style={{ textAlign: "right" }}>{num(r.signups)}</td>
                <td className={adminTableCellClass} style={{ textAlign: "right" }}>
                  <AdminPill tone={r.signups > 0 ? "success" : "neutral"}>
                    {pct(r.conversion_rate)}
                  </AdminPill>
                </td>
              </tr>
            ))}
          </AdminTable>
        ) : (
          <AdminEmptyState
            icon={Compass}
            title="No attributed traffic yet"
            description="Once visitors land with utm_source / utm_medium / utm_campaign on the URL, sessions and conversion rate per source will appear here."
          />
        )}
      </AdminSection>

      {/* ── 2 — Funnel per landing page ──────────────────────────────── */}
      <AdminSection
        title="02 — Funnel by landing page"
        description="Pageviews → CTA clicks → signups → activated (first export within 14 days). Shows where the funnel leaks per entry page."
      >
        {data && data.funnel.length > 0 ? (
          <AdminTable
            head={
              <>
                <th className={adminTableHeadCellClass}>Landing page</th>
                <th className={adminTableHeadCellClass} style={{ textAlign: "right" }}>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="cursor-help border-b border-dotted border-muted-foreground/40">Sessions</span>
                      </TooltipTrigger>
                      <TooltipContent>Unique sessions per landing page</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </th>
                <th className={adminTableHeadCellClass} style={{ textAlign: "right" }}>CTA sessions</th>
                <th className={adminTableHeadCellClass} style={{ textAlign: "right" }}>Signups</th>
                <th className={adminTableHeadCellClass} style={{ textAlign: "right" }}>Activated</th>
                <th className={adminTableHeadCellClass} style={{ textAlign: "right" }}>PV → Signup</th>
              </>
            }
          >
            {data.funnel.map((r, i) => {
              const pvToSignup = r.pageviews ? r.signups / r.pageviews : 0;
              return (
                <tr key={i} className={adminTableRowClass}>
                  <td className={adminTableCellClass} style={{ fontFamily: "ui-monospace, monospace", fontSize: 12 }}>
                    {r.path || "/"}
                  </td>
                  <td className={adminTableCellClass} style={{ textAlign: "right" }}>{num(r.pageviews)}</td>
                  <td className={adminTableCellClass} style={{ textAlign: "right" }}>{num(r.cta_sessions)}</td>
                  <td className={adminTableCellClass} style={{ textAlign: "right" }}>{num(r.signups)}</td>
                  <td className={adminTableCellClass} style={{ textAlign: "right" }}>{num(r.activated)}</td>
                  <td className={adminTableCellClass} style={{ textAlign: "right" }}>
                    <span className="text-muted-foreground">{pct(pvToSignup)}</span>
                  </td>
                </tr>
              );
            })}
          </AdminTable>
        ) : (
          <AdminEmptyState
            icon={TrendingUp}
            title="No funnel data yet"
            description="Once visitors view landing pages, click CTAs, sign up, and export their first proposal within 14 days, the full funnel per page will appear here."
          />
        )}
      </AdminSection>

      {/* ── 3 — Page performance ─────────────────────────────────────── */}
      <AdminSection
        title="03 — Page performance"
        description="Views, average dwell time, and average max scroll depth per page. Scroll below 40% is flagged — that's the signal content isn't pulling people down."
      >
        {data && data.pages.length > 0 ? (
          <AdminTable
            head={
              <>
                <th className={adminTableHeadCellClass}>Page</th>
                <th className={adminTableHeadCellClass} style={{ textAlign: "right" }}>Views</th>
                <th className={adminTableHeadCellClass} style={{ textAlign: "right" }}>Avg dwell</th>
                <th className={adminTableHeadCellClass} style={{ textAlign: "right" }}>Avg scroll</th>
              </>
            }
          >
            {data.pages.map((r, i) => {
              const lowScroll = r.avg_scroll_pct != null && r.avg_scroll_pct < 40;
              return (
                <tr key={i} className={adminTableRowClass}>
                  <td className={adminTableCellClass} style={{ fontFamily: "ui-monospace, monospace", fontSize: 12 }}>
                    {r.path || "/"}
                  </td>
                  <td className={adminTableCellClass} style={{ textAlign: "right" }}>{num(r.views)}</td>
                  <td className={adminTableCellClass} style={{ textAlign: "right" }}>{dwellLabel(r.avg_dwell_seconds)}</td>
                  <td className={adminTableCellClass} style={{ textAlign: "right" }}>
                    {r.avg_scroll_pct == null ? (
                      <span className="text-muted-foreground">—</span>
                    ) : (
                      <AdminPill tone={lowScroll ? "warning" : "neutral"}>{r.avg_scroll_pct}%</AdminPill>
                    )}
                  </td>
                </tr>
              );
            })}
          </AdminTable>
        ) : (
          <AdminEmptyState
            icon={FileText}
            title="No pageview data yet"
            description="Pageviews, dwell time, and scroll depth will populate here as visitors browse the marketing site."
          />
        )}
      </AdminSection>

      {/* ── 4 — CTA breakdown ────────────────────────────────────────── */}
      <AdminSection
        title="04 — CTA breakdown"
        description="Clicks per CTA type and surface. Conversion rate is signups attributed to sessions that clicked this CTA."
      >
        {data && data.ctas.length > 0 ? (
          <AdminTable
            head={
              <>
                <th className={adminTableHeadCellClass}>CTA</th>
                <th className={adminTableHeadCellClass}>Location</th>
                <th className={adminTableHeadCellClass} style={{ textAlign: "right" }}>Clicks</th>
                <th className={adminTableHeadCellClass} style={{ textAlign: "right" }}>Sessions</th>
                <th className={adminTableHeadCellClass} style={{ textAlign: "right" }}>Signups</th>
                <th className={adminTableHeadCellClass} style={{ textAlign: "right" }}>Conv. rate</th>
              </>
            }
          >
            {data.ctas.map((r, i) => (
              <tr key={i} className={adminTableRowClass}>
                <td className={adminTableCellClass}>
                  <AdminPill tone="accent">{r.cta_type}</AdminPill>
                </td>
                <td className={adminTableCellClass}>{r.location}</td>
                <td className={adminTableCellClass} style={{ textAlign: "right" }}>{num(r.clicks)}</td>
                <td className={adminTableCellClass} style={{ textAlign: "right" }}>{num(r.unique_sessions)}</td>
                <td className={adminTableCellClass} style={{ textAlign: "right" }}>{num(r.signups_attributed)}</td>
                <td className={adminTableCellClass} style={{ textAlign: "right" }}>
                  <AdminPill tone={r.signups_attributed > 0 ? "success" : "neutral"}>
                    {pct(r.conversion_rate)}
                  </AdminPill>
                </td>
              </tr>
            ))}
          </AdminTable>
        ) : (
          <AdminEmptyState
            icon={MousePointerClick}
            title="No CTA clicks yet"
            description="As visitors click 'Get started', 'Book a demo', and other tracked CTAs, click counts and conversion rates per surface will appear here."
          />
        )}
      </AdminSection>

      {/* ── 5 — Device split ─────────────────────────────────────────── */}
      <AdminSection
        title="05 — Device split"
        description="Sessions and conversion by device class."
      >
        {data && data.devices.length > 0 ? (
          <AdminTable
            head={
              <>
                <th className={adminTableHeadCellClass}>Device</th>
                <th className={adminTableHeadCellClass} style={{ textAlign: "right" }}>Sessions</th>
                <th className={adminTableHeadCellClass} style={{ textAlign: "right" }}>Signups</th>
                <th className={adminTableHeadCellClass} style={{ textAlign: "right" }}>Conv. rate</th>
              </>
            }
          >
            {data.devices.map((r, i) => (
              <tr key={i} className={adminTableRowClass}>
                <td className={adminTableCellClass} style={{ textTransform: "capitalize" }}>{r.device}</td>
                <td className={adminTableCellClass} style={{ textAlign: "right" }}>{num(r.sessions)}</td>
                <td className={adminTableCellClass} style={{ textAlign: "right" }}>{num(r.signups)}</td>
                <td className={adminTableCellClass} style={{ textAlign: "right" }}>
                  <AdminPill tone={r.signups > 0 ? "success" : "neutral"}>{pct(r.conversion_rate)}</AdminPill>
                </td>
              </tr>
            ))}
          </AdminTable>
        ) : (
          <AdminEmptyState
            icon={Smartphone}
            title="No device data yet"
            description="Device class is captured per session entry; this table fills in as visitors arrive."
          />
        )}
      </AdminSection>

      {/* ── 6 — Country split ────────────────────────────────────────── */}
      <AdminSection
        title="06 — Country split"
        description="Sessions and conversion by visitor country, enriched server-side from edge headers."
      >
        {data && data.country && data.country.length > 0 ? (
          <AdminTable
            head={
              <>
                <th className={adminTableHeadCellClass}>Country</th>
                <th className={adminTableHeadCellClass} style={{ textAlign: "right" }}>Sessions</th>
                <th className={adminTableHeadCellClass} style={{ textAlign: "right" }}>Signups</th>
                <th className={adminTableHeadCellClass} style={{ textAlign: "right" }}>Conv. rate</th>
              </>
            }
          >
            {data.country.map((r, i) => (
              <tr key={i} className={adminTableRowClass}>
                <td className={adminTableCellClass}>
                  <span className="text-foreground">{r.country_name}</span>
                  <span className="ml-2 text-muted-foreground font-mono text-[11px]">{r.country_code}</span>
                </td>
                <td className={adminTableCellClass} style={{ textAlign: "right" }}>{num(r.sessions)}</td>
                <td className={adminTableCellClass} style={{ textAlign: "right" }}>{num(r.signups)}</td>
                <td className={adminTableCellClass} style={{ textAlign: "right" }}>
                  <AdminPill tone={r.signups > 0 ? "success" : "neutral"}>{pct(r.conversion_rate)}</AdminPill>
                </td>
              </tr>
            ))}
          </AdminTable>
        ) : (
          <AdminEmptyState
            icon={Globe}
            title="No country data yet"
            description="Country is captured per session entry from edge headers; existing rows pre-deploy remain unknown."
          />
        )}
      </AdminSection>
    </div>
  );
}
